import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from 'reactstrap';

import { messages } from '../../i18n';

import { RESOURCE_TYPES } from '../../constants';

import types from './utils/types';

import './ContentDropdownMenu.scss';

const propTypes = {

	/**
	 * Configuration settings for the resource types to use in Content Builder.
	 */
	config: types.config.isRequired,

	/**
	 * An optional label for the button to toggle open the dropdown menu.
	 */
	placeholderLabel: PropTypes.string,

	/**
	 * Callback function to handle creating a new piece of content.
	 */
	onCreate: PropTypes.func.isRequired,

};

const ContentDropdownMenu = (props) => {

	const {
		config,
		placeholderLabel,
		onCreate,
	} = props;

	const intl = useIntl();

	const [isOpen, setIsOpen] = useState(false);

	const contentOptionsList = useMemo(() => {

		const integrationItems = [];
		const contentItems = [];

		if (config?.[RESOURCE_TYPES.MIRO]) {

			integrationItems.push({
				type: RESOURCE_TYPES.MIRO,
				label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_LABEL_TEXT_MIRO),
				description: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DESCRIPTION_TEXT_MIRO),
				image: 'https://d328ser7ogqmui.cloudfront.net/images/miro-logo.png',
			});

		}

		if (config?.[RESOURCE_TYPES.SLIDE]) {

			contentItems.push({
				type: RESOURCE_TYPES.SLIDE,
				label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_LABEL_TEXT_SLIDE),
				description: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DESCRIPTION_TEXT_SLIDE),
				icon: 'fas fa-align-left',
			});

		}

		if (config?.[RESOURCE_TYPES.IMAGE]) {

			contentItems.push({
				type: RESOURCE_TYPES.IMAGE,
				label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_LABEL_TEXT_IMAGE),
				description: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DESCRIPTION_TEXT_IMAGE),
				icon: 'fas fa-image',
			});

		}

		if (config?.[RESOURCE_TYPES.VIDEO]) {

			contentItems.push({
				type: RESOURCE_TYPES.VIDEO,
				label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_LABEL_TEXT_VIDEO),
				description: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DESCRIPTION_TEXT_VIDEO),
				icon: 'fas fa-video',
			});

		}

		if (config?.[RESOURCE_TYPES.EMBED]) {

			contentItems.push({
				type: RESOURCE_TYPES.EMBED,
				label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_LABEL_TEXT_EMBED),
				description: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DESCRIPTION_TEXT_EMBED),
				icon: 'fas fa-window-maximize',
			});

		}

		if (config?.[RESOURCE_TYPES.FORM]) {

			contentItems.push({
				type: RESOURCE_TYPES.FORM,
				label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_LABEL_TEXT_FORM),
				description: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DESCRIPTION_TEXT_FORM),
				icon: 'fas fa-list-alt',
			});

		}

		if (config?.[RESOURCE_TYPES.POLL]) {

			contentItems.push({
				type: RESOURCE_TYPES.POLL,
				label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_LABEL_TEXT_POLL),
				description: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DESCRIPTION_TEXT_POLL),
				icon: 'fas fa-poll',
			});

		}

		if (config?.[RESOURCE_TYPES.QUIZ]) {

			contentItems.push({
				type: RESOURCE_TYPES.QUIZ,
				label: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_LABEL_TEXT_QUIZ),
				description: intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DESCRIPTION_TEXT_QUIZ),
				icon: 'fas fa-question-square',
			});

		}

		const result = [];

		if (integrationItems.length > 0) {

			result.push({
				id: 'integrations',
				label: intl.formatMessage(
					messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DROPDOWN_GROUP_LEGEND_INTEGRATIONS
				),
				items: integrationItems,
			});

		}

		if (contentItems.length > 0) {

			result.push({
				id: 'content',
				label: intl.formatMessage(
					messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_DROPDOWN_GROUP_LEGEND_CONTENT
				),
				items: contentItems,
			});

		}

		return result;

	}, [config, intl]);

	const intlPlaceholderLabel =
		placeholderLabel || intl.formatMessage(messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_BUTTON_TEXT_ADD_CONTENT);

	return (
		<div className="content-builder-dropdown-menu">
			<Dropdown
				isOpen={isOpen}
				toggle={() => setIsOpen((prev) => !prev)}>
				<DropdownToggle
					color="neutral"
					className="content-builder-dropdown-toggle"
					outline>
					<span>
						<i
							aria-hidden="true"
							className="fal fa-circle-plus" />
						{' '}
						{intlPlaceholderLabel}
					</span>
				</DropdownToggle>
				<DropdownMenu>
					{contentOptionsList.map((group) => (
						<Fragment key={group.id}>
							<DropdownItem
								className="content-builder-dropdown-header"
								header>
								{group.label}
							</DropdownItem>
							{group.items.map((content) => (
								<DropdownItem
									key={content.type}
									className="content-builder-dropdown-item d-flex align-items-center"
									onClick={(event) => onCreate(event, content.type)}>
									{content.icon &&
										<div>
											<span
												aria-hidden="true"
												className="fa-stack">
												<i className="fas fa-square fa-stack-2x"></i>
												<i className={`${content.icon} fa-stack-1x fa-inverse`}></i>
											</span>
										</div>
									}
									{content.image &&
										<div className="content-builder-dropdown-item-img-container">
											<img
												alt=""
												src={content.image} />
										</div>
									}
									<div className="d-flex flex-column lh-sm">
										<strong>{content.label}</strong>
										<span>{content.description}</span>
									</div>
								</DropdownItem>
							))}
						</Fragment>
					))}
				</DropdownMenu>
			</Dropdown>
		</div>
	);

};

ContentDropdownMenu.propTypes = propTypes;

export default ContentDropdownMenu;
